import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { useSubscription } from "hooks/useStripe";
import { Feature, useFeatureFlags } from "context/FeatureFlag";

export const TrialBanner = () => {
  const { t } = useTranslation();
  const { uiState, subscription } = useSubscription();
  const { isFeatureEnabled } = useFeatureFlags();
  const location = useLocation();

  if (
    uiState !== "trial_active" ||
    !isFeatureEnabled(Feature.Billing) ||
    location.pathname === "/settings/billing"
  ) {
    return null;
  }

  const daysLeft = Math.max(
    0,
    Math.ceil(
      ((subscription?.trailEnd || 0) * 1000 - Date.now()) / (1000 * 60 * 60 * 24)
    )
  );

  const isUrgent = daysLeft <= 3;

  return (
    <div
      className={`relative overflow-hidden rounded-lg mb-6 p-4 ${
        isUrgent
          ? "bg-gradient-to-r from-amber-500 to-orange-500"
          : "bg-gradient-to-r from-cyan-500 to-blue-500"
      }`}
    >
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white" />
        <div className="absolute -right-16 -bottom-16 h-56 w-56 rounded-full bg-white" />
      </div>

      <div className="relative flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              isUrgent ? "bg-amber-400/30" : "bg-white/20"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-white"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.828a1 1 0 101.415-1.414L11 9.586V6z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          <div>
            <p className="text-sm font-semibold text-white">
              {t("settings.billing.trial_banner", { days: daysLeft })}
            </p>
            <p className="text-xs text-white/70 mt-0.5">
              {t("settings.billing.trial_banner_subtitle")}
            </p>
          </div>
        </div>

        <Link
          to="/settings/billing"
          className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold shadow-sm transition whitespace-nowrap ${
            isUrgent
              ? "bg-white text-orange-600 hover:bg-orange-50"
              : "bg-white text-blue-600 hover:bg-blue-50"
          }`}
        >
          {t("settings.billing.trial_banner_cta")}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </Link>
      </div>
    </div>
  );
};
